<template>
  <div class="Field-Multiple-Img">
    <div class="Field-Multiple-Img__wrapper" 
      @dragover.prevent="changeDragAndDrop(true)"
      @dragleave="changeDragAndDrop(false)" 
      @dragenter="onDragEnter" 
      @drop="onDrop" 
      :class="get_modifyWrapperClass"
    >
      <div class="Field-Multiple-Img__load" @click="openFileManager" v-if="uploadedImages.length === 0 && oldImages.length === 0">
        <div class="Field-Multiple-Img__load-static" v-if="!dragging">
          <button class="Field-Multiple-Img__btn" type="button">
            <MiniatureIcon /> {{ props.text }}
          </button>
          <p class="Field-Multiple-Img__txt">{{ props.subtext }}</p>
        </div>
        <p class="Field-Multiple-Img__drag-hint" v-else>Перенесите изображения сюда</p>
      </div>
      <div class="Field-Multiple-Img__result" v-if="uploadedImages.length || oldImages.length">
        <div v-for="(image, index) in oldImages" :key="index" class="Field-Multiple-Img__image-container">
          <button class="Field-Multiple-Img__remove-img" @click="toRemoveOldImage(index)" type="button">
            <CrossIcon />
          </button>
          <img class="Field-Multiple-Img__img" :src="image" />
        </div>
        <div v-for="(image, index) in uploadedImages" :key="index" class="Field-Multiple-Img__image-container">
          <LoaderCircle class="Field-Multiple-Img__ldr" :color="'white'" :size="16" v-if="isLoadingImg"/>
          <button class="Field-Multiple-Img__remove-img" v-if="!isLoadingImg" @click="toRemoveImage(index)" type="button">
            <CrossIcon />
          </button>
          <img class="Field-Multiple-Img__img" :src="image.preview" />
        </div>
        <button class="Field-Multiple-Img__add-new-images" type="button" @click="openFileManager">
          <PlusIcon /> Добавить
        </button>
      </div>
      <input 
        :name="props.name" 
        type="file" 
        accept=".jpg,.jpeg,.png,.webp" 
        ref="imageInput" 
        @change="handleFileChange" 
        v-show="false" 
        multiple 
      />
      <template v-for="image in oldImages">
        <input :name="props.oldImagesFieldName" :value="image" hidden/>
      </template>
    </div>
    <p class="Field-Multiple-Img__error">{{ props.error }}</p>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Props } from "./scripts/props";
import CrossIcon from '@svg/CrossIcon.vue';
import PlusIcon from '@svg/PlusIcon.vue';
import MiniatureIcon from '@svg/MiniatureIcon.vue';
import LoaderCircle from '@ui/Loaders/LoaderCircle/LoaderCircle.vue';

const props = defineProps(Props());
const emit = defineEmits(['imageUploaded']);

const uploadedImages = ref([]);
const oldImages = ref([]);
const dragging = ref(false);
const isLoadingImg = ref(false);
const imageInput = ref(null);
let dragCounter = 0;

// Храним оригинальные файлы в Map для гарантии уникальности
const filesMap = ref(new Map());

onMounted(() => {
  if(props.startImages){
    oldImages.value = [...props.startImages];
    emit('updateOldImages', oldImages.value);
  }
  if(!props.initData) return;  
  processInitialImages(props.initData);
});

const get_modifyWrapperClass = computed(() => {
  return props.error ? 'Field-Single-Img__wrapper--error' : dragging.value ? 'Field-Single-Img__wrapper--dragging' : '';
});

const openFileManager = () => {
  if (imageInput.value) {
    imageInput.value.value = '';
  }
  imageInput.value.click();
};

const handleFileChange = async (event) => {
  const newFiles = Array.from(event.target.files);
  if (!newFiles.length) return;
  
  await processNewFiles(newFiles);
};

const processNewFiles = async (files) => {
  // Фильтруем только уникальные файлы
  const uniqueFiles = files.filter(file => {
    const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
    return !filesMap.value.has(fileKey);
  });

  if (!uniqueFiles.length) return;

  // Добавляем в Map
  uniqueFiles.forEach(file => {
    const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
    filesMap.value.set(fileKey, file);
  });

  updateInputFiles();
  await createImagePreviews(uniqueFiles);
};

const createImagePreviews = async (files) => {
  isLoadingImg.value = true;
  
  try {
    const newPreviews = await Promise.all(
      files.map(file => createImagePreview(file))
    );
    
    uploadedImages.value = [...uploadedImages.value, ...newPreviews];
    emitImageUploaded();
  } catch (error) {
    console.error('Error processing images:', error);
  } finally {
    isLoadingImg.value = false;
  }
};

const createImagePreview = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve({ file, preview: e.target.result });
    reader.readAsDataURL(file);
  });
};

const toRemoveImage = (index) => {
  const removedImage = uploadedImages.value.splice(index, 1)[0];
  const fileKey = `${removedImage.file.name}-${removedImage.file.size}-${removedImage.file.lastModified}`;
  
  filesMap.value.delete(fileKey);
  updateInputFiles();
  emitImageUploaded();
};

const toRemoveOldImage = (index) => {
  oldImages.value.splice(index, 1)[0];
  emit('updateOldImages', oldImages.value);
}

const updateInputFiles = () => {
  const dataTransfer = new DataTransfer();
  filesMap.value.forEach(file => dataTransfer.items.add(file));
  imageInput.value.files = dataTransfer.files;
};

const emitImageUploaded = () => {
  emit('imageUploaded', Array.from(filesMap.value.values()));
};

const onDragEnter = () => {
  dragCounter++;
  dragging.value = true;
};

const changeDragAndDrop = (status) => {
  dragging.value = status;
  status ? dragCounter++ : dragCounter--;
  if (dragCounter === 0) dragging.value = false;
};

const onDrop = async (event) => {
  event.preventDefault();
  dragging.value = false;
  const newFiles = Array.from(event.dataTransfer.files);
  if (!newFiles.length) return;
  
  await processNewFiles(newFiles);
};

const processInitialImages = (files) => {
  files.forEach(file => {
    const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
    filesMap.value.set(fileKey, file);
  });
  
  updateInputFiles();
  Promise.all(files.map(file => createImagePreview(file)))
    .then(images => {
      uploadedImages.value = images;
      emitImageUploaded();
    })
    .catch(error => {
      console.error('Error processing initial images:', error);
    });
};
</script>

<style lang="scss">
  @import "./styles/styles";
</style>